import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, Text, BlockStack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { sendCreateCustomerQuestionnaire } from "services/customers.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";

export interface CustomerQuestionnaireCrossCompanyConfirmProps {
  companyId: string;
  customerId: string;
  year: number;
  sourceCompanyName: string;
  attachmentCount: number;
  onSuccess: () => void;
}

const CustomerQuestionnaireCrossCompanyConfirm: React.FC<
  CustomerQuestionnaireCrossCompanyConfirmProps
> = ({
  companyId,
  customerId,
  year,
  sourceCompanyName,
  attachmentCount,
  onSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitKind, setSubmitKind] = useState<"copy" | "fresh" | null>(null);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const submitQuestionnaire = async (
    kind: "copy" | "fresh",
    options: {
      confirmCrossCompanyPrefill?: boolean;
      sendFreshQuestionnaireForm?: boolean;
    },
    errorMessage: string
  ) => {
    setErrMsg(null);
    setSubmitKind(kind);
    setIsLoading(true);
    try {
      await sendCreateCustomerQuestionnaire(companyId, customerId, year, options);
      dispatch(fetchCustomer({ customerId, companyId }));
      onSuccess();
      closeModal();
    } catch (e) {
      console.error(errorMessage, e);
      setErrMsg("error");
    } finally {
      setIsLoading(false);
      setSubmitKind(null);
    }
  };

  const handleConfirm = () =>
    submitQuestionnaire(
      "copy",
      { confirmCrossCompanyPrefill: true },
      "Error sending questionnaire with cross-company copy"
    );

  const handleSendFresh = () =>
    submitQuestionnaire(
      "fresh",
      { sendFreshQuestionnaireForm: true },
      "Error sending fresh questionnaire"
    );

  return (
    <BlockStack gap="300">
      {errMsg && (
        <Banner tone="critical">{t(errMsg)}</Banner>
      )}
      <Text as="p">
        {t("questionnaire_cross_company_confirm_message", {
          company: sourceCompanyName || "—",
          attachments:
            attachmentCount > 0
              ? t("questionnaire_cross_company_attachments_suffix", {
                  count: attachmentCount,
                })
              : t("questionnaire_cross_company_attachments_none"),
        })}
      </Text>
      <InlineStack align="end" gap="200">
        <Button
          onClick={handleSendFresh}
          disabled={isLoading}
          loading={isLoading && submitKind === "fresh"}
        >
          {t("questionnaire_send_fresh_form")}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          loading={isLoading && submitKind === "copy"}
          disabled={isLoading}
        >
          {t("confirm_copy_and_send")}
        </Button>
      </InlineStack>
    </BlockStack>
  );
};

export default CustomerQuestionnaireCrossCompanyConfirm;
